/**
 * MainMenu — VU Player Pro inspired home screen.
 *
 *  ┌──────────────────────────────────────────────────────────────────┐
 *  │ Hero (last-watched or featured movie, full backdrop)              │
 *  │                                                                  │
 *  │   Title                                                          │
 *  │   Year · Genre · Duration                                        │
 *  │   ▶ Play                                                         │
 *  │                                                                  │
 *  ├──────────────────────────────────────────────────────────────────┤
 *  │  ┌────┐  ┌────┐  ┌────┐  ┌────┐  ┌────┐                          │
 *  │  │📺  │  │🎬  │  │🎞   │  │↻    │  │🔍  │   ← 5 pill nav buttons │
 *  │  │LIVE│  │MOV │  │SERI│  │CATCH│  │SRCH│      (active = white   │
 *  │  └────┘  └────┘  └────┘  └────┘  └────┘       border)            │
 *  ├──────────────────────────────────────────────────────────────────┤
 *  │  Last Added Movies                                               │
 *  │  [poster] [poster] [poster] [poster] [poster] →                  │
 *  └──────────────────────────────────────────────────────────────────┘
 *
 *  Layout faithfully replicates vupro's XCHomeActivity / activity_mobile_home
 *  (com.vupurple.player 2.3). Hero auto-selects:
 *    1. most-recent watch-history item that has a poster, OR
 *    2. a random "movie" with a logo_url, OR
 *    3. a neutral placeholder gradient.
 *
 *  The 5-pill nav row mirrors vupro's primary navigation row. The "Last Added"
 *  rail uses the tail of the movies array (playlists are time-ordered).
 */
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { PLAYER } from "../lib/testIds";
import { isAndroid } from "../lib/deviceId";
import { isTvLayout } from "../lib/tvDetect";
import { getHistory } from "../lib/watchHistory";
import { getFavorites } from "../lib/favorites";
import { subscribe as subscribeWatched, isWatched } from "../lib/watchedItems";
import { fetchXtreamSeriesEpisodes } from "../lib/xtreamApi";
import { Tv, Film, Clapperboard, History as HistoryIcon, Search, Play } from "lucide-react";
import { useCachedImage } from "../lib/imageCache";

// ---------------------------------------------------------------------------
// Single pill nav button — icon above text, white border when active.
// Mirrors vupro's primary navigation row.
// ---------------------------------------------------------------------------
function NavPill({ icon: Icon, label, onClick, active, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`group flex flex-col items-center justify-center gap-1.5 px-4 py-3 sm:py-4 rounded-2xl border-2 transition-colors outline-none focus-within-outline-inset focus-visible:z-10 relative ${
        active
          ? "border-white bg-white/10"
          : "border-white/15 hover:border-white/40 hover:bg-white/5"
      }`}
    >
      <Icon
        className="w-6 h-6 sm:w-7 sm:h-7 text-white"
        strokeWidth={1.8}
      />
      <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-white">
        {label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Last-added rail card — square poster + name on hover/focus.
// ---------------------------------------------------------------------------

// v1.4.72 — Decode a series id from a synthetic favorite stream_url
// like `series://12345` or `xtream://series/12345`. Returns null when
// the URL doesn't carry one (e.g. M3U-only playlists).
function decodeSeriesIdFromUrl(url) {
  if (!url) return null;
  const s = String(url);
  const m1 = s.match(/^series:\/\/(.+)$/i);
  if (m1) return m1[1];
  const m2 = s.match(/^xtream:\/\/series\/(.+)$/i);
  if (m2) return m2[1];
  return null;
}

function RailCard({ item, onClick }) {
  const src = useCachedImage(item.logo_url);
  return (
    <button
      onClick={onClick}
      data-testid={`rail-card-${item.stream_url || item.name}`}
      className="group shrink-0 w-32 sm:w-40 outline-none rounded-xl focus-within-outline-inset"
    >
      <div className="relative aspect-[2/3] rounded-xl overflow-hidden bg-[#2a1145] ring-1 ring-white/10 group-hover:ring-white/30">
        {item.logo_url ? (
          <img
            src={src}
            alt={item.name}
            referrerPolicy="no-referrer"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#4a2a73] to-[#1f0936] flex items-center justify-center">
            <Film className="w-7 h-7 text-white/30" />
          </div>
        )}
      </div>
      <div className="mt-1.5 text-[11px] sm:text-xs text-white/85 truncate" title={item.name}>
        {item.name}
      </div>
    </button>
  );
}

// v1.2.0 — Continue Watching / global search removed per user request.
// Rail card renderer below is the ONLY one used now (the "Recently added
// favourite episodes" section).

export default function MainMenu() {
  const navigate = useNavigate();
  const outletCtx = useOutletContext() || {};
  const playlists = Array.isArray(outletCtx.playlists) ? outletCtx.playlists : [];
  const playlistData = usePlaylistData();
  const channels = Array.isArray(playlistData?.channels) ? playlistData.channels : [];
  const status = playlistData?.status || "idle";
  const xtreamCreds = playlistData?.xtream?.creds || null;
  const tv = isTvLayout();

  // v1.3.61 — watched-tick subscription. Bumps when the watchedItems
  // registry changes (user finished an episode), so the favoriteEpisodes
  // memo below re-runs and drops the now-watched row from the rail.
  const [watchedTick, setWatchedTick] = useState(0);
  useEffect(() => {
    const off = subscribeWatched(() => setWatchedTick((t) => t + 1));
    return off;
  }, []);

  // Hero — pick the best candidate (last-watched with poster > random movie
  // with poster > nothing). Recomputed on every render so the hero refreshes
  // each time the user returns to the home screen.
  const hero = useMemo(() => {
    const history = getHistory();
    const watched = history.find((h) => h.logo_url && h.content_type !== "live_tv");
    if (watched) return { ...watched, source: "history" };
    const movies = channels.filter((c) => c.content_type === "movie" && c.logo_url);
    if (movies.length === 0) return null;
    // Deterministic-ish pick: middle of the list
    return { ...movies[Math.floor(movies.length / 2)], source: "featured" };
  }, [channels]);

  // v1.4.72 — Recently added FAVOURITE EPISODES rail.
  //
  // The Xtream-First architecture (v1.3.x+) stopped inlining
  // episode-level rows into the `channels` array, so we can't
  // just filter — we have to lazy-fetch each favourited show's
  // full episode list via `get_series_info`. The result is cached
  // in `sessionStorage` (keyed by playlist + series_id) so the
  // rail is instant on subsequent Home visits.
  //
  // Per user rules:
  //   • Show up to 5 NEWEST episodes per favourited show.
  //   • DROP episodes the user has already marked as watched.
  //   • Across shows, order the visible flat list by each show's
  //     newest-unwatched episode's added_ts (freshest drops first).
  const [favoriteEpisodes, setFavoriteEpisodes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    async function build() {
      try {
        const favs = getFavorites("series") || [];
        if (!favs.length || !xtreamCreds) {
          setFavoriteEpisodes([]);
          return;
        }
        const playlistId = playlistData?.playlist?.id || "default";
        const cacheKey = (sid) => `zapp_fav_eps_v1__${playlistId}__${sid}`;
        // Build a per-show map of the 5 newest UNWATCHED episodes.
        const groups = [];
        for (const f of favs) {
          const sid = f.series_id || decodeSeriesIdFromUrl(f.stream_url);
          if (!sid) continue;
          let episodes = null;
          let seriesCover = null;
          // Prefer cached (fast); refresh in background if older than 6 h.
          try {
            const cached = sessionStorage.getItem(cacheKey(sid));
            if (cached) {
              const parsed = JSON.parse(cached);
              // Back-compat: old cache was just an array of episodes.
              if (Array.isArray(parsed)) {
                episodes = parsed;
              } else if (parsed && typeof parsed === "object") {
                episodes = parsed.episodes || [];
                seriesCover = parsed.seriesCover || null;
              }
            }
          } catch { /* ignore */ }
          if (!episodes) {
            try {
              const info = await fetchXtreamSeriesEpisodes(xtreamCreds, sid);
              episodes = info?.episodes || [];
              seriesCover =
                info?.info?.cover ||
                info?.info?.cover_big ||
                info?.info?.movie_image ||
                null;
              try {
                sessionStorage.setItem(
                  cacheKey(sid),
                  JSON.stringify({ episodes, seriesCover }),
                );
              } catch { /* quota */ }
            } catch {
              episodes = [];
            }
            if (cancelled) return;
          }
          if (!episodes.length) continue;
          // Sort DESC by (season, episode) → newest first.
          const sorted = episodes.slice().sort((a, b) => {
            const s = (b.season || 0) - (a.season || 0);
            if (s !== 0) return s;
            return (b.episode || 0) - (a.episode || 0);
          });
          const unwatched = sorted.filter((ep) => !isWatched(ep.stream_url));
          if (!unwatched.length) continue;
          // Poster fallback chain: episode's own still → main show poster
          // from series info → favourite's cached logo. This fixes blank
          // tiles on the "Recently added episodes" rail when the provider
          // omits per-episode `movie_image` (e.g. Binnelanders S01E47).
          const showPoster = seriesCover || f.logo_url || null;
          const window = unwatched.slice(0, 5).map((ep) => ({
            ...ep,
            content_type: "series",
            series_id: sid,
            series_name: f.series_name || f.name || "",
            logo_url: ep.logo_url || showPoster,
          }));
          groups.push({
            newestTs: Number(window[0].added_ts || 0),
            items: window,
          });
        }
        if (cancelled) return;
        groups.sort((a, b) => b.newestTs - a.newestTs);
        setFavoriteEpisodes(groups.flatMap((g) => g.items));
      } catch (e) {
        console.warn("[MainMenu] favoriteEpisodes fetch failed:", e);
        if (!cancelled) setFavoriteEpisodes([]);
      }
    }
    build();
    return () => {
      cancelled = true;
    };
    // watchedTick makes the rail refresh after the user marks / unmarks
    // an episode watched (drops or restores the row).
  }, [xtreamCreds, watchedTick, playlistData?.playlist?.id]);

  // Always-valid renderable URL for the hero poster — cached data URL
  // when available, original provider URL otherwise.
  const heroSrc = useCachedImage(hero?.logo_url);

  const fmtCount = (n) => (n > 0 ? n.toLocaleString() : status === "loading" ? "…" : "—");

  const handleHeroPlay = () => {
    if (!hero) return;
    // Watch.jsx reads `stream_url` from the query params (NOT `url`).
    // Sending the wrong name silently broke playback and looked like
    // an app crash — fixed in v1.3.32.
    if (!hero.stream_url) {
      const target = hero.content_type === "series" ? "series" : "movie";
      navigate(`/player/browse/${target}`);
      return;
    }
    const params = new URLSearchParams({
      stream_url: hero.stream_url,
      name: hero.name || "Resume",
      type: hero.content_type || "movie",
    });
    if (hero.logo_url) params.set("logo", hero.logo_url);
    navigate(`/player/watch?${params.toString()}`);
  };

  // Counts for the pill subtitles (shown only on TV / desktop where there is
  // room — mobile shows just labels to keep the pills compact).
  //
  // v1.4.27 — For series we tally UNIQUE shows rather than raw episode
  // rows. IPTV providers commonly ship every episode as its own M3U
  // entry so a Xtream catalog with 15 000 series can inflate to
  // 400 000+ "series"-typed rows once episodes are counted. The
  // Series page groups those episodes back into series tiles, so the
  // main-menu badge should match what the user actually sees inside
  // (unique series count), not the internal row count.
  const counts = useMemo(() => {
    const out = { live_tv: 0, movie: 0, series: 0, catchup: 0 };
    const seenSeries = new Set();
    for (const ch of channels) {
      if (ch.tv_archive) out.catchup += 1;
      if (ch.content_type === "live_tv" || ch.content_type === "movie") {
        out[ch.content_type] += 1;
      } else if (ch.content_type === "series") {
        // Prefer an explicit series-level identifier when present, then
        // series_name (episodes share it), then the stream URL (series
        // pseudo-URLs from the get_series harvest look like
        // "xtream://series/{id}").
        const key =
          (ch.xc_kind === "series" && ch.xc_id && `xc:${ch.xc_id}`) ||
          (ch.series_name && `nm:${ch.series_name.toLowerCase().trim()}`) ||
          ch.stream_url ||
          ch.name ||
          "";
        if (key && !seenSeries.has(key)) {
          seenSeries.add(key);
          out.series += 1;
        }
      }
    }
    return out;
  }, [channels]);

  return (
    <div
      data-testid={PLAYER.mainMenu}
      data-tv={tv ? "true" : "false"}
      className="space-y-6 sm:space-y-8"
    >
      {/* ============================================================
          HERO — last watched or featured movie
         ============================================================ */}
      <div
        data-testid="home-hero"
        className="relative overflow-hidden rounded-3xl bg-[#1a0524] ring-1 ring-white/10"
        style={{ aspectRatio: tv ? "42/9" : "32/7" }}
      >
        {hero?.logo_url ? (
          <>
            {/* Blurred backdrop fills the card. */}
            <img
              src={heroSrc}
              alt=""
              referrerPolicy="no-referrer"
              className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-70"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            {/* Sharp poster on the right side. */}
            <img
              src={heroSrc}
              alt={hero.name}
              referrerPolicy="no-referrer"
              className="hidden md:block absolute right-6 top-1/2 -translate-y-1/2 h-[88%] aspect-[2/3] object-cover rounded-2xl ring-2 ring-white/20 shadow-2xl"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#3e1657] via-[#1a0524] to-[#0f0218]" />
        )}

        {/* Dark gradient overlay so text is always readable. */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0524] via-[#1a0524]/85 to-transparent" />

        {/* Foreground content. */}
        <div className="relative h-full flex flex-col justify-end p-5 sm:p-8 md:p-10 max-w-[680px]">
          <p className="uppercase tracking-[0.25em] text-[10px] sm:text-xs text-[#ff4d9d] mb-2">
            {hero?.source === "history" ? "Continue watching" : "Featured"}
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white drop-shadow-lg line-clamp-2">
            {hero?.name || "Welcome to ZA Player Pro"}
          </h2>
          <p className="mt-2 text-sm text-white/70 line-clamp-2 max-w-md">
            {hero
              ? hero.content_type === "movie"
                ? "Pick up where you left off, or browse the catalogue."
                : "Tap Play to start watching."
              : status === "loading"
              ? "Updating your playlist — this only takes a moment."
              : playlists.length === 0
              ? "No playlist assigned yet. Ask your provider to assign one from the admin panel."
              : "Pick a section below to start streaming."}
          </p>
          <div className="mt-5 flex items-center gap-3">
            {hero ? (
              <button
                data-testid="home-hero-play-btn"
                onClick={handleHeroPlay}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#C62828] hover:bg-[#a51e1e] active:scale-95 transition-all text-white font-semibold shadow-xl shadow-black/40 outline-none focus-visible:ring-4 focus-visible:ring-[#C62828]/50"
              >
                <Play className="w-5 h-5 fill-white" strokeWidth={0} />
                <span>Play</span>
              </button>
            ) : (
              <button
                data-testid="home-hero-browse-btn"
                onClick={() => navigate("/player/browse/movie")}
                disabled={playlists.length === 0}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#C62828] hover:bg-[#a51e1e] disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed active:scale-95 transition-all text-white font-semibold shadow-xl shadow-black/40"
              >
                {playlists.length === 0 ? "No playlist" : "Browse Movies"}
              </button>
            )}
            {playlists.length > 0 && (
              <span className="text-xs text-white/50">
                {playlists.length} playlist{playlists.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ============================================================
          5-PILL NAVIGATION — Live / Movies / Series / Catch Up / Search
          (mirrors vupro's primary nav row exactly)
         ============================================================ */}
      <div className="grid grid-cols-5 gap-2 sm:gap-3">
        <NavPill
          testId={PLAYER.tileLiveTv}
          icon={Tv}
          label={tv ? `Live · ${fmtCount(counts.live_tv)}` : "Live"}
          onClick={() => navigate("/player/browse/live_tv")}
        />
        <NavPill
          testId={PLAYER.tileMovies}
          icon={Film}
          label={tv ? `Movies · ${fmtCount(counts.movie)}` : "Movies"}
          onClick={() => navigate("/player/browse/movie")}
        />
        <NavPill
          testId={PLAYER.tileSeries}
          icon={Clapperboard}
          label={tv ? `Series · ${fmtCount(counts.series)}` : "Series"}
          onClick={() => navigate("/player/browse/series")}
        />
        <NavPill
          testId={PLAYER.tileCatchup}
          icon={HistoryIcon}
          label="Catch Up"
          onClick={() => navigate("/player/catchup")}
        />
        <NavPill
          testId={PLAYER.tileSearch}
          icon={Search}
          label="Search"
          onClick={() => navigate("/player/search")}
        />
      </div>

      {/* ============================================================
          "Recently Favorites episodes added" rail — only renders when
          the user has favourited at least one show. Shows the freshest
          episodes for those shows, sorted by added_ts desc. Empty
          state nudges them to favourite something from the series
          browser. v1.3.30 — replaces the old "Recently added movies"
          rail per admin request.
         ============================================================ */}
      {favoriteEpisodes.length > 0 ? (
        <section data-testid="home-recently-favorites" className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white">
              Recently added episodes (Favorites)
            </h3>
            <button
              onClick={() => navigate("/player/browse/series")}
              data-testid="home-see-all-series"
              className="text-xs text-white/60 hover:text-white"
            >
              See all →
            </button>
          </div>
          <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 -mx-4 px-4 snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {favoriteEpisodes.map((item) => (
              <div key={item.stream_url} className="snap-start">
                <RailCard
                  item={item}
                  onClick={() => {
                    // v1.4.72 — Each rail card is a REAL episode now
                    // (fetched via get_series_info), so we can play it
                    // directly. Fall back to the series drill-down if
                    // the episode somehow lacks a playable stream_url.
                    if (!item.stream_url) {                      const name = item.series_name || item.name || "";
                      if (name) {
                        navigate(
                          `/player/browse/series?series=${encodeURIComponent(name)}`
                        );
                      }
                      return;
                    }
                    navigate("/player/watch", {
                      state: {
                        stream_url: item.stream_url,
                        name: item.name,
                        logo: item.logo_url || "",
                        type: "series",
                        series_id: item.series_id || null,
                        series_name: item.series_name || null,
                        season: item.season || null,
                        episode: item.episode || null,
                        poster: item.poster_url || item.logo_url || "",
                      },
                    });
                  }}
                />
              </div>
            ))}
          </div>
        </section>
      ) : channels.length > 0 ? (
        <section data-testid="home-recently-favorites-empty" className="space-y-2">
          <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white">
            Recently added episodes (Favorites)
          </h3>
          <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-6 text-sm text-white/60 text-center">
            Favourite a show from the Series page to see the latest
            episodes here.
            <button
              onClick={() => navigate("/player/browse/series")}
              className="ml-2 text-[#22d3ee] hover:underline"
            >
              Browse series →
            </button>
          </div>
        </section>
      ) : null}

      {/* v1.3.31: "Recently added movies" rail removed per admin request. */}

      {/* Loading hint */}
      {status === "loading" && favoriteEpisodes.length === 0 && (
        <p className="text-center text-sm text-white/50">
          Updating your playlist… your channels will appear shortly.
        </p>
      )}

      {/* If the user has multiple assigned playlists, expose a tiny
          switcher (read-only — playlists themselves are admin-managed).
          When only one playlist is assigned, no UI is shown so the home
          stays clean like vupro. */}
      {!tv && !isAndroid() && playlists.length > 1 && (
        <div className="text-center">
          <button
            data-testid="home-change-playlist"
            onClick={() => navigate("/player/playlists")}
            className="text-xs text-white/50 hover:text-white underline-offset-2 hover:underline"
          >
            Switch playlist ({playlists.length} assigned)
          </button>
        </div>
      )}
    </div>
  );
}